import React, {
  useMemo, useRef, useState,
} from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, string } from 'prop-types'

import TagsContent from 'Pods/SearchPage/Shared/TagsContent/TagsContent'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { addTag, removeTag } from 'Pods/SearchPage/Store/actions/tags'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useIsomorphicLayoutEffect from 'Utils/useIsomorphicLayoutEffect'

// Upper bound on chips rendered for measurement; the fixed-width container decides how many of
// them stay visible. The rest live in the "More" dropdown.
const MAX_CANDIDATES = 8
// Server render has no layout to measure against, so it shows the count the narrowest lg
// container (340px) is known to fit; hydration remeasures before first paint.
const SSR_VISIBLE_COUNT = 2
// Mirrors the `gap` of .tags-filter__inline.
const CHIP_GAP = 8

// Selected tags are cleared by setAssetType (user-initiated changes only), not an effect here -
// popstate restores set assetType and the hydrated tags together, and an effect-based reset
// would wipe the restored tags right back out.
function TagSuggestions({ suggestedTags = EMPTY_ARRAY }) {
  const t = useTranslate()

  const assetType = useSearchPageStore(state => state.assetType)
  const selectedTags = useSearchPageStore(state => state.tags)

  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags])

  // Suggestions keep the backend order so toggling an inline chip never reorders the row (a
  // selected chip that jumped to the front shifted every other chip sideways). Only selected
  // tags the backend no longer suggests are prepended, so they stay reachable; Set dedupes.
  const orderedTags = useMemo(() => {
    const suggestedSet = new Set(suggestedTags)
    const unsuggestedSelected = selectedTags.filter(tag => !suggestedSet.has(tag))
    return Array.from(new Set([...unsuggestedSelected, ...suggestedTags]))
  }, [selectedTags, suggestedTags])

  const candidates = useMemo(() => orderedTags.slice(0, MAX_CANDIDATES), [orderedTags])

  // The container hugs its chips inside a per-breakpoint cap (see .secondary-filters__tags), so
  // the search field beside it takes the slack and the row's left edge never moves when the
  // backend swaps the suggestions. Chips are fitted by measurement: a measuring pass renders
  // every candidate plus the More trigger, which opens the container to whatever the cap and the
  // row allow, records the widths, then hides the chips past that edge - all inside a layout
  // effect, so the pass never paints. The fit only ever reads the container during that pass:
  // once it hugs, its width is the fitted content, which would feed a rounding loop back into
  // the fit. More is only reserved once the full row is known not to fit without it.
  const rootRef = useRef(null)
  const inlineRef = useRef(null)
  const moreRef = useRef(null)
  const chipWidthsRef = useRef(new Map())
  const moreWidthRef = useRef(0)
  const [isMeasuring, setIsMeasuring] = useState(false)
  const [visibleCount, setVisibleCount] = useState(SSR_VISIBLE_COUNT)

  // assetType is a dep because it swaps the filters on the bar's left side (poly/technical vs
  // print attributes) in the same commit, which changes how much the row leaves this box. The
  // suggestions that follow the switch only land once the search response does, and setAssetType
  // clears tags to a constant reference, so `candidates` alone would not remeasure until then.
  useIsomorphicLayoutEffect(() => {
    setIsMeasuring(true)
  }, [candidates, assetType])

  useIsomorphicLayoutEffect(() => {
    const rootEl = rootRef.current
    if (!isMeasuring || !rootEl) return

    Array.from(inlineRef.current?.children ?? []).forEach((chip, index) => {
      if (candidates[index] !== undefined && chip.offsetWidth > 0) {
        chipWidthsRef.current.set(candidates[index], chip.offsetWidth)
      }
    })
    if (moreRef.current?.offsetWidth > 0) {
      moreWidthRef.current = moreRef.current.offsetWidth
    }

    const fit = () => {
      const fullWidth = rootEl.clientWidth
      const widths = []
      for (let index = 0; index < candidates.length; index += 1) {
        const width = chipWidthsRef.current.get(candidates[index])
        if (width === undefined) {
          // Below lg the row is display:none, so chips measure 0 and are not cached; the
          // viewport resize that reveals the row triggers the pass that measures them.
          setVisibleCount(0)
          return
        }
        widths.push(width)
      }

      const rowWidth = count => widths
        .slice(0, count)
        .reduce((sum, width, index) => sum + width + (index > 0 ? CHIP_GAP : 0), 0)
      const needsMore = orderedTags.length > candidates.length
        || rowWidth(candidates.length) > fullWidth
      const available = needsMore ? fullWidth - CHIP_GAP - moreWidthRef.current : fullWidth
      let count = 0
      while (count < candidates.length && rowWidth(count + 1) <= available) count += 1
      setVisibleCount(count)
    }

    fit()
    setIsMeasuring(false)
  }, [candidates, orderedTags, isMeasuring])

  // A hugging container cannot report new room on its own, so the viewport resize is what
  // reruns the measuring pass (it also covers the row appearing at lg).
  useIsomorphicLayoutEffect(() => {
    const remeasure = () => setIsMeasuring(true)
    window.addEventListener('resize', remeasure)
    return () => window.removeEventListener('resize', remeasure)
  }, [])

  const inlineTags = isMeasuring ? candidates : candidates.slice(0, visibleCount)
  const hasOverflow = isMeasuring
    ? orderedTags.length > 0
    : orderedTags.length > inlineTags.length
  const hasSelectedAnyFilters = selectedTags.length > 0

  const handleToggle = (tag) => {
    if (selectedSet.has(tag)) {
      removeTag(tag)
    } else {
      addTag(tag)
    }
  }

  const dropdownContent = (
    <div className="tags-filter__dropdown">
      <TagsContent />
    </div>
  )

  return (
    <div
      ref={rootRef}
      className="tags-filter"
      role="group"
      aria-label={t('search_page.filters.tags.tags_group_label')}
    >
      <div ref={inlineRef} className="tags-filter__inline">
        {inlineTags.map(tag => (
          <Button
            key={tag}
            type="secondary"
            isActive={selectedSet.has(tag)}
            aria-pressed={selectedSet.has(tag)}
            onClick={() => handleToggle(tag)}
            customClass="filter-wrapper__button tags-filter__chip--inline"
          >
            {tag}
          </Button>
        ))}
      </div>

      {hasOverflow && (
        <div ref={moreRef} className="tags-filter__more">
          <FiltersWrapper
            placeholder={t('search_page.filters.tags.more')}
            content={dropdownContent}
            hasSelectedAnyFilters={hasSelectedAnyFilters}
            closingDropdownClassName="filter-wrapper"
          />
        </div>
      )}
    </div>
  )
}

TagSuggestions.propTypes = {
  suggestedTags: arrayOf(string),
}

export default TagSuggestions
